import { getRunSummaries, MAX_RUN_SUMMARY_IDS } from '../services/run.service';
import type { RunSummaryLookup } from '../types/run.api.types';

import {
  isCapabilityAvailable,
  isMissingRoute,
  isValidationOfField,
  markCapabilityUnavailable,
} from './catalog-capabilities';

/** The answer for every id once this session knows the route is missing (an older deploy). */
const UNAVAILABLE: RunSummaryLookup = { available: false };

interface Waiter {
  resolve: (lookup: RunSummaryLookup) => void;
  reject: (error: unknown) => void;
}

/** Ids asked for since the last flush (lower-cased), each with every caller waiting on it. */
let pending = new Map<string, Waiter[]>();
let flushScheduled = false;

/**
 * An older catalog-service has no `/catalog-runs/summaries`: it routes the path to
 * `/catalog-runs/{run_id}` and answers 422 on `path.run_id` (or 404/405 behind a proxy).
 */
function isSummariesRouteMissing(error: unknown): boolean {
  return isMissingRoute(error, (apiError) => isValidationOfField(apiError, 'path.run_id'));
}

/** Reads one ≤ 50-id chunk and settles every waiter in it — found, omitted, or route missing. */
async function readChunk(ids: string[], waiters: Map<string, Waiter[]>): Promise<void> {
  const settle = (id: string, lookup: RunSummaryLookup) => {
    waiters.get(id)?.forEach((w) => {
      w.resolve(lookup);
    });
  };
  if (!isCapabilityAvailable('runSummaries')) {
    ids.forEach((id) => {
      settle(id, UNAVAILABLE);
    });
    return;
  }
  try {
    const items = await getRunSummaries(ids);
    const byId = new Map(items.map((item) => [item.id.toLowerCase(), item]));
    ids.forEach((id) => {
      settle(id, { available: true, summary: byId.get(id) ?? null });
    });
  } catch (error) {
    if (isSummariesRouteMissing(error)) {
      // No toast: callers switch to the per-row run + progress reads for the rest of the session.
      markCapabilityUnavailable('runSummaries');
      ids.forEach((id) => {
        settle(id, UNAVAILABLE);
      });
      return;
    }
    ids.forEach((id) => {
      waiters.get(id)?.forEach((w) => {
        w.reject(error);
      });
    });
  }
}

/** Sends everything queued this tick as ≤ 50-id summaries calls (one call for a typical page). */
function flush(): void {
  const batch = pending;
  pending = new Map();
  flushScheduled = false;
  const ids = [...batch.keys()];
  for (let start = 0; start < ids.length; start += MAX_RUN_SUMMARY_IDS) {
    void readChunk(ids.slice(start, start + MAX_RUN_SUMMARY_IDS), batch);
  }
}

/**
 * One run's summary row, coalesced with every other id requested in the same tick into ONE
 * `GET catalog-runs/summaries` call — so a page of N history rows / run-name links makes 1 request.
 * Use it as a per-id `queryFn` (cached per id under `runSummary(id)`); resolves `available: false`
 * without a request once this session has seen the route missing.
 */
export function loadRunSummary(runId: string): Promise<RunSummaryLookup> {
  if (!isCapabilityAvailable('runSummaries')) return Promise.resolve(UNAVAILABLE);
  return new Promise<RunSummaryLookup>((resolve, reject) => {
    const key = runId.toLowerCase();
    pending.set(key, [...(pending.get(key) ?? []), { resolve, reject }]);
    if (!flushScheduled) {
      flushScheduled = true;
      // A macrotask, not a microtask: every query a single React commit mounts has started by then.
      setTimeout(flush, 0);
    }
  });
}
